import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import StarRating from "@/components/StarRating";
import MilestoneMedia from "@/components/MilestoneMedia";

const BADGE_CLASS = {
  "Not Started": "badge-status badge-notstarted",
  "In Progress": "badge-status badge-progress",
  Complete: "badge-status badge-complete",
};

const Section = ({ eyebrow, title, children, testId, divider = true }) => (
  <section className="senk-rise" data-testid={testId}>
    {divider && <hr className="senk-rule mb-16" />}
    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
      <div className="md:col-span-3">
        <p className="label-eyebrow">{eyebrow}</p>
        {title && (
          <h2 className="font-serif-display text-3xl sm:text-4xl mt-4 leading-[1.05]" style={{ color: "var(--senk-fg)" }}>
            {title}
          </h2>
        )}
      </div>
      <div className="md:col-span-9">{children}</div>
    </div>
  </section>
);

const PortalMilestoneRow = ({ milestone, index, slug, showCaptions }) => {
  const [expanded, setExpanded] = useState(false);
  const [hasMedia, setHasMedia] = useState(false);
  const [checked, setChecked] = useState(false);

  // Probe whether this milestone has any media so we only show the expander when relevant
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get(`/portal/${slug}/milestones/${index}/media`);
        if (alive) {
          setHasMedia((res.data || []).length > 0);
          setChecked(true);
        }
      } catch { if (alive) setChecked(true); }
    })();
    return () => { alive = false; };
  }, [slug, index]);

  return (
    <li
      className="py-6"
      style={{ borderTop: index === 0 ? "none" : "0.5px solid var(--senk-border)" }}
      data-testid="milestone-item"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-baseline gap-5 min-w-0">
          <span className="font-serif-display italic text-lg" style={{ color: "var(--senk-body)" }}>
            {String(index + 1).padStart(2, "0")}
          </span>
          <span className="font-serif-display text-xl md:text-2xl truncate" style={{ color: "var(--senk-fg)" }}>
            {milestone.name}
          </span>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <span data-testid="milestone-date" className="label-eyebrow">
            Due {new Date(milestone.due_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
          </span>
          <span data-testid="milestone-status" className={BADGE_CLASS[milestone.status] || BADGE_CLASS["Not Started"]}>
            {milestone.status}
          </span>
        </div>
      </div>
      {checked && hasMedia && (
        <div className="mt-3 pl-0 sm:pl-12">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            data-testid={`milestone-toggle-${index}`}
            className="label-eyebrow flex items-center gap-1 hover:opacity-100"
            style={{ opacity: 0.8 }}
          >
            {expanded ? "Hide media" : "View media"}
            <ChevronDown size={12} strokeWidth={2} style={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
          </button>
          {expanded && (
            <div className="mt-3">
              <MilestoneMedia slug={slug} milestoneIndex={index} editable={false} showCaptions={showCaptions} />
            </div>
          )}
        </div>
      )}
    </li>
  );
};

export default function Portal() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Feedback state
  const [fbName, setFbName] = useState("");
  const [feedback, setFeedback] = useState("");
  const [fbSubmitting, setFbSubmitting] = useState(false);
  const [fbDone, setFbDone] = useState(false);

  // Testimonial state
  const [testimonial, setTestimonial] = useState("");
  const [rating, setRating] = useState(0);
  const [tSubmitting, setTSubmitting] = useState(false);
  const [tDone, setTDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/portal/${slug}`);
        setData(res.data);
      } catch (e) {
        if (e.response?.status === 404) setNotFound(true);
        else toast.error("Could not load project");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const submitFeedback = async (e) => {
    e.preventDefault();
    if (!feedback.trim()) {
      toast.error("Write something first");
      return;
    }
    setFbSubmitting(true);
    try {
      await api.post(`/portal/${slug}/feedback`, {
        message: feedback.trim(),
        name: fbName.trim() || null,
      });
      setFeedback("");
      setFbName("");
      setFbDone(true);
    } catch {
      toast.error("Could not send feedback");
    } finally {
      setFbSubmitting(false);
    }
  };

  const submitTestimonial = async (e) => {
    e.preventDefault();
    if (!testimonial.trim()) {
      toast.error("Add a short note");
      return;
    }
    if (rating < 1 || rating > 5) {
      toast.error("Choose a rating");
      return;
    }
    setTSubmitting(true);
    try {
      await api.post(`/portal/${slug}/testimonial`, { message: testimonial.trim(), rating });
      setTestimonial("");
      setRating(0);
      setTDone(true);
    } catch {
      toast.error("Could not submit testimonial");
    } finally {
      setTSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="portal-loading" style={{ background: "var(--senk-bg)" }}>
        <Loader2 className="animate-spin" size={24} strokeWidth={1.25} style={{ color: "var(--senk-fg)" }} />
      </div>
    );
  }

  if (notFound) {
    return (
      <main className="min-h-screen flex items-center justify-center px-6" style={{ background: "var(--senk-bg)", color: "var(--senk-fg)" }}>
        <div className="max-w-md text-center senk-rise" data-testid="portal-not-found">
          <p className="label-eyebrow">SENK DESIGN</p>
          <h1 className="font-serif-display text-5xl mt-4 leading-tight">Portal not found.</h1>
          <p className="mt-6" style={{ color: "var(--senk-body)" }}>
            This link may have been archived. Please contact the studio.
          </p>
        </div>
      </main>
    );
  }

  if (!data) return null;
  const { project, milestones } = data;
  const drive_url = project.drive_url;

  return (
    <main className="min-h-screen" style={{ background: "var(--senk-bg)", color: "var(--senk-fg)" }}>
      {/* Top bar */}
      <header style={{ borderBottom: "0.5px solid var(--senk-border)" }}>
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-6 flex items-center justify-between">
          <a href="https://senkdesign.com" target="_blank" rel="noopener noreferrer" className="brand-mark" data-testid="studio-name" style={{ textDecoration: "none" }}>
            SENK DESIGN
          </a>
          <span className="label-eyebrow hidden sm:block">Client Portal · SS26</span>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28 space-y-24 md:space-y-32">

        {/* Hero / Project Brief */}
        <section className="senk-rise" data-testid="project-brief">
          <p className="label-eyebrow">Project Brief — 01</p>
          <h1
            className="font-serif-display text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[1.02]"
            data-testid="project-name"
            style={{ color: "var(--senk-fg)" }}
          >
            {project.brand_name ? `${project.brand_name} — ${project.name}` : project.name}
          </h1>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 mt-16">
            <div className="md:col-span-7 space-y-12">
              <div>
                <p className="label-eyebrow">Scope</p>
                <p
                  className="mt-4 text-lg md:text-xl leading-relaxed"
                  style={{ color: "var(--senk-body)" }}
                  data-testid="project-scope"
                >
                  {project.scope}
                </p>
              </div>
              <div>
                <p className="label-eyebrow">Timeline</p>
                <p
                  className="mt-3 font-serif-display text-2xl"
                  data-testid="project-timeline"
                  style={{ color: "var(--senk-fg)" }}
                >
                  {project.timeline}
                </p>
              </div>
            </div>
            <div className="md:col-span-5">
              <p className="label-eyebrow">What&apos;s Included</p>
              <ul className="mt-4 space-y-3" data-testid="project-included">
                {project.included.map((item, i) => (
                  <li
                    key={i}
                    className="flex gap-4 text-sm md:text-base"
                    style={{ color: "var(--senk-fg)" }}
                  >
                    <span className="font-serif-display italic w-6 shrink-0" style={{ color: "var(--senk-body)" }}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Milestones */}
        <Section eyebrow="Milestones — 02" title="Phases &amp; status" testId="milestones-section">
          <ul style={{ borderTop: "0.5px solid var(--senk-border)", borderBottom: "0.5px solid var(--senk-border)" }}>
            {milestones.map((m, i) => (
              <PortalMilestoneRow key={i} milestone={m} index={i} slug={slug} showCaptions={project.show_captions !== false} />
            ))}
          </ul>
        </Section>

        {/* Files */}
        <Section eyebrow="Files — 03" title="Project drive" testId="files-section">
          <p className="text-base md:text-lg leading-relaxed max-w-xl" style={{ color: "var(--senk-body)" }}>
            All deliverables, references, and working files live in a single shared
            Google Drive folder. Access is always on.
          </p>
          <a
            href={drive_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.preventDefault();
              window.open(drive_url, "_blank", "noopener,noreferrer");
            }}
            data-testid="files-drive-button"
            className="btn-primary btn-type mt-10"
          >
            <span>Open Google Drive</span>
            <ArrowUpRight size={16} strokeWidth={2} />
          </a>
        </Section>

        {/* Feedback */}
        <Section eyebrow="Feedback — 04" title="A note to the studio" testId="feedback-section">
          <p className="text-base md:text-lg leading-relaxed max-w-xl" style={{ color: "var(--senk-body)" }}>
            A direction shift, a reference, a worry. Send it any time. Only the studio sees it.
          </p>
          <form onSubmit={submitFeedback} className="mt-10 max-w-2xl">
            <input
              type="text"
              data-testid="feedback-name"
              value={fbName}
              onChange={(e) => setFbName(e.target.value)}
              placeholder="Your name (optional)"
              className="senk-input"
            />
            <textarea
              data-testid="feedback-input"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={4}
              placeholder="Write to the studio…"
              className="senk-input resize-none mt-6"
            />
            <div className="flex items-center justify-between mt-8 flex-wrap gap-4">
              {fbDone ? (
                <span className="inline-confirm" data-testid="feedback-confirm">Received.</span>
              ) : (
                <span className="label-eyebrow">Private to studio</span>
              )}
              <button
                type="submit"
                disabled={fbSubmitting}
                data-testid="feedback-submit"
                className="btn-primary btn-type"
              >
                {fbSubmitting && <Loader2 size={16} className="animate-spin" />}
                <span>Send Feedback</span>
              </button>
            </div>
          </form>
        </Section>

        {/* Testimonial */}
        <Section eyebrow="Testimonial — 05" title="Share your experience" testId="testimonial-section">
          <form onSubmit={submitTestimonial} className="max-w-2xl">
            <p className="text-base md:text-lg leading-relaxed" style={{ color: "var(--senk-body)" }}>
              If you&apos;d like, leave a short reflection on working with SENK DESIGN.
            </p>

            <div className="mt-10">
              <p className="label-eyebrow">Your rating</p>
              <div className="mt-4">
                <StarRating value={rating} onChange={setRating} readOnly={tDone} />
              </div>
            </div>

            <div className="mt-10">
              <p className="label-eyebrow">Your words</p>
              <textarea
                data-testid="testimonial-input"
                value={testimonial}
                onChange={(e) => setTestimonial(e.target.value)}
                rows={4}
                placeholder="Working with SENK DESIGN was…"
                className="senk-input resize-none mt-3"
              />
            </div>

            <div className="flex items-center justify-between mt-8 flex-wrap gap-4">
              {tDone ? (
                <span className="inline-confirm" data-testid="testimonial-confirm">Thank you.</span>
              ) : (
                <span className="label-eyebrow">Shared with studio only</span>
              )}
              <button
                type="submit"
                disabled={tSubmitting || tDone}
                data-testid="testimonial-submit"
                className="btn-primary btn-type"
              >
                {tSubmitting && <Loader2 size={16} className="animate-spin" />}
                <span>Share Experience</span>
              </button>
            </div>
          </form>
        </Section>
      </div>

      <footer style={{ borderTop: "0.5px solid var(--senk-border)" }} className="mt-24">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-10 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <span className="label-eyebrow">SENK DESIGN · {new Date().getFullYear()}</span>
          <a
            href="/admin"
            className="label-eyebrow hover:opacity-100 transition-opacity"
            style={{ opacity: 0.7 }}
            data-testid="footer-admin-link"
          >
            Studio access →
          </a>
        </div>
      </footer>
    </main>
  );
}
